<template>
  <div class="storage-box flex-center">
    <div class="dashboard-bar progress-box">
      <p>{{ displayName }}</p>
      <div class="progress" role="progressbar" :aria-label="$t('plans.usageProgressLabel')" :aria-valuenow="usagePercentage" aria-valuemin="0" aria-valuemax="100">
        <div class="progress-bar" :class="getPercentageClass()" :style="`width: ${usagePercentage}%`"></div>
      </div>
      <router-link class="btn btn-outline-secondary" to="/plans">
        <IconArrowUp/>
      </router-link>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.storage-box {
  flex-direction:                       column;
  gap:                                  5px;
  // padding:                              11px 10px;
  // margin-right:                         10px;

  p {
    color:                              var(--Brand-Primary);
    text-align:                         center;
    font-size:                          var(--font-size-10);
    font-weight:                        500;
    line-height:                        normal;
    letter-spacing:                     2.15px;
    text-transform:                     uppercase;
    margin:                             0;
  }

  .progress-box {
    margin:                             0;
  }
}

.dashboard-bar {
  @include                              border_radius(30px);
  display:                              flex;
  flex-direction:                       row;
  justify-self:                         center;
  align-items:                          center;
  width:                                auto !important;
  height:                               36px;
  padding:                              4px 4px 4px 10px;
  gap:                                  10px;
  background:                           var(--Shades-W);
  border:                               1.5px solid var(--Shades-30-Border);

  p {
    color:                              var(--Brand-Secondary);
    font-size:                          var(--font-size-10);
    font-weight:                        500;
    line-height:                        normal;
    letter-spacing:                     1px;
    text-transform:                     uppercase;
    margin:                             0;
  }

  .btn-outline-secondary {
    @include                            border_radius(0 30px 30px 0);
    height:                             100%;
    min-height:                         unset;
    padding:                            0 7px;
    margin:                             0;
    border:                             0;
    border-left:                        1.5px solid var(--Shades-30-Border);
    background-color:                   unset;

    :deep(svg) {
      * {
        stroke:                         var(--Brand-Primary);
      }
    }
  }
}

.progress-box {
  text-align:                           center;
  margin-bottom:                        5px;
  .progress {
    width:                              60px;
    height:                             13px;
    border:                             0.5px solid $gainsboro;
  }
  .progress-bar {
    @include                            border_radius(var(--bs-progress-border-radius));
  }
  a {
    color:                              var(--Brand-Primary) !important;
    font-size:                          var(--font-size-10);
    font-weight:                        500;
    text-transform:                     uppercase;
    span {
      font-weight:                      400;
      text-transform:                   lowercase;
    }
  }
}

header {
  .progress-box {
    a {
      span {
        display:                        none;
      }
    }
  }
}

@media (max-width: 991px) {
  .storage-box {
    flex-direction:                     row;
    margin:                             0;
    padding:                            0 10px;
    background:                         var(--Shades-10);
  }

  .dashboard-bar {
    width:                              100% !important;
    padding:                            0;
    border:                             0;

    .btn {
      display:                          none;
    }
  }

  .progress-box {
    .progress {
      width:                            100%;
    }
  }
}
</style>

<script>
import { useUserStore } from '@/store/user'
import { useSubscriptionStore } from '@/store/subscription'

export default {
  name: 'MenuStorageBar',
  data: () => ({
    loading: false,
  }),
  computed: {
    userStore() {
      return useUserStore();
    },
    subscriptionStore() {
      return useSubscriptionStore();
    },
    session() {
      return this.userStore.sessionInfo;
    },
    displayName() {
      return this.subscriptionStore.clinicUsage.displayName || this.$i18n.t('plans.freePlan');
    },
    usagePercentage() {
      return this.subscriptionStore.clinicUsage.percentage || 0;
    }
  },
  async mounted() {
    const usage = await this.subscriptionStore.updateUsage(this.session.currentClinic.country);

    // Signal set by the backend (clinic-subscription.js), computed from the
    // database only (legacy Subscription.periodEndDate vs. now) - true for
    // clinics not yet migrated to the new Stripe account whose last known
    // paid period has already ended and who haven't declined to resubscribe
    // (migrationStatus "declined", set from the plan page's own opt-out
    // button). Clinics with no subscription at all (free tier, the vast
    // majority of clients) never get this set.
    // Only forced once per login session (this component remounts on every
    // route change) - after that, the user is free to browse elsewhere and
    // decide not to resubscribe.
    if (usage?.needsResubscription && !this.userStore.resubscriptionPromptShown) {
      this.userStore.markResubscriptionPromptShown();
      this.$router.push('/plans');
    }

    // Same signal, earlier stage: the legacy subscription is still within its
    // paid period, so there's nothing to force - just a heads-up that it will
    // need to be renewed manually when it does end. Shown once per login,
    // wherever the user happens to be, since it's not tied to visiting /plans.
    const legacyNotExpiredYet = usage?.migrated === false && usage?.isExpired === false && usage?.subscription;
    if (legacyNotExpiredYet && !this.userStore.migrationNoticeShown) {
      this.userStore.markMigrationNoticeShown();
      const validUntil = usage.subscription.current_period_end
        ? new Date(usage.subscription.current_period_end * 1000).toLocaleDateString(this.$i18n.locale, {
            day: '2-digit', month: 'long', year: 'numeric'
          })
        : 'N/A';
      // Lib has no queue - a new toast kills whatever's showing. Delay past
      // the default 3s duration to avoid colliding with one already visible.
      setTimeout(() => {
        this.$message.popup(this, 'plans.migrationNotice.text', 'plans.migrationNotice.title', 'warn', {
          duration: 10000, params: { date: validUntil }
        });
      }, 3500);
    }
  },
  methods: {
    getPercentageClass() {
      return this.$dashboardService.getPercentageClass(this.usagePercentage);
    }
  },
}
</script>
